import { MapPin, Phone, Clock, Mail } from "lucide-react";
import { Cloud, Sparkle, SectionTitle, Reveal } from "./Decor";
import { WhatsAppButton } from "./WhatsApp";

const Row = ({ icon: Icon, tone, label, children, testId, delay }) => (
  <Reveal delay={delay}>
    <div className="flex gap-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-sky-100" data-testid={testId}>
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tone}`}><Icon className="h-5 w-5" /></span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <div className="mt-1 break-words text-base font-medium text-slate-800">{children}</div>
      </div>
    </div>
  </Reveal>
);

export const ContactSection = ({ c, s }) => {
  const clinic = c?.clinic || {};
  const labels = clinic.labels || {};
  const timings = (clinic.timings || []).filter((t) => t.days || t.time);
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(clinic.map_query || clinic.address || "")}&output=embed`;
  return (
    <section id="contact" className="relative overflow-hidden bg-gradient-to-b from-sky-100 to-sky-50 py-16 sm:py-20" data-testid="contact-section">
      <Cloud className="drift absolute -left-10 top-6 w-44 text-white" />
      <Sparkle className="absolute right-[10%] top-14 h-6 w-6 text-yellow-300" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle id="contact" title={s.title} subtitle={s.subtitle} />
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <Row icon={MapPin} tone="bg-pink-100 text-pink-600" label={labels.address || "Clinic address"} testId="contact-address">
              <p className="font-semibold text-blue-900">{c?.profile?.clinic_name}</p>
              {clinic.name && <p>{clinic.name}</p>}
              <p className="text-slate-600">{clinic.address}</p>
            </Row>
            {clinic.phone && (
              <Row icon={Phone} tone="bg-sky-100 text-sky-700" label={labels.phone || "Contact number"} testId="contact-phone" delay={0.05}>
                <a href={`tel:${clinic.phone}`} className="text-blue-900 underline-offset-4 hover:underline">{clinic.phone}</a>
              </Row>
            )}
            {clinic.email && (
              <Row icon={Mail} tone="bg-purple-100 text-purple-600" label={labels.email || "Email"} testId="contact-email" delay={0.08}>
                <a href={`mailto:${clinic.email}`} className="text-blue-900 underline-offset-4 hover:underline">{clinic.email}</a>
              </Row>
            )}
            {timings.length > 0 && (
              <Row icon={Clock} tone="bg-yellow-100 text-orange-600" label={labels.timings || "Consultation timings"} testId="contact-timings" delay={0.1}>
                {timings.map((t, i) => (
                  <p key={t.id || i}>
                    <span className="text-slate-600">{t.days}:</span> <span className="font-semibold text-blue-900">{t.time}</span>
                  </p>
                ))}
                {clinic.note && <p className="mt-1 text-sm text-slate-500">{clinic.note}</p>}
              </Row>
            )}
            <Reveal delay={0.15}>
              <WhatsAppButton wa={c?.whatsapp} testId="contact-whatsapp-btn" className="w-full text-base sm:w-auto" />
            </Reveal>
          </div>
          {clinic.show_map !== false && (clinic.map_query || clinic.address) && (
            <Reveal delay={0.1} className="overflow-hidden rounded-[2rem] border-4 border-white bg-white shadow-md">
              <iframe title="Clinic location map" data-testid="contact-map" src={mapSrc} loading="lazy" className="h-72 w-full lg:h-full lg:min-h-[420px]" referrerPolicy="no-referrer-when-downgrade" />
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
